import React, { useState, useEffect } from "react";
import { Box, Typography, TextField } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotificationService } from "../notification";

import { LoadingButton } from "@mui/lab";
import {
  apiGetSettings,
  apiUpdateConversionRate,
  apiUpdateTokenCost,
} from "../../api/users";

const AddWallet = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationService();

  const [tokenRate, setTokenRate] = useState("");
  const [conversionRates, setConversionRates] = useState({
    job: "",
    shortlisting: "",
    medic: "",
    mobility: "",
    performance: "",
    reports: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => apiGetSettings(),
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (data?.data) {
      setTokenRate(data.data?.rates?.token || "");
      setConversionRates({
        job: data.data?.walletsMeta?.rates?.job || "",
        shortlisting: data.data?.walletsMeta?.rates?.shortlisting || "",
        medic: data.data?.walletsMeta?.rates?.medic || "",
        mobility: data.data?.walletsMeta?.rates?.mobility || "",
        performance: data.data?.walletsMeta?.rates?.performance || "",
        reports: data.data?.walletsMeta?.rates?.reports || "",
      });
    }
  }, [data]);

  const tokenMutation = useMutation({
    mutationFn: (newRate: string) => apiUpdateTokenCost(newRate),
    onSuccess: (data) => {
      if (data?.status === "error") {
        showNotification(data?.msg || "Failed to update token rate", "error");
        return;
      }
      showNotification("Token rate updated successfully", "success");
      queryClient.invalidateQueries({
        queryKey: ["settings"],
        exact: true,
      });
    },
    onError: () => {
      showNotification("Failed to update token rate", "error");
    },
  });

  const conversionMutation = useMutation({
    mutationFn: (rates: typeof conversionRates) =>
      apiUpdateConversionRate(rates),
    onSuccess: (data) => {
      if (data?.status === "error") {
        showNotification(
          data?.msg || "Failed to update conversion rates",
          "error",
        );
        return;
      }
      showNotification("Conversion rates updated successfully", "success");
      queryClient.invalidateQueries({
        queryKey: ["settings"],
        exact: true,
      });
    },
    onError: () => {
      showNotification("Failed to update conversion rates", "error");
    },
  });

  if (isLoading) return <p>Loading...</p>;

  const formatKey = (key: string) => {
    const formatted = key.charAt(0).toUpperCase() + key.slice(1);
    return formatted === "Shortlisting" ? "Hire" : formatted;
  };

  return (
    <Box>
      {/* Token Rate */}
      <Box
        sx={{
          p: 3,
          borderRadius: 1,
          maxWidth: "80%",
          my: 4,
          boxShadow: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography sx={{ minWidth: 200 }}>Token Rate</Typography>
          <TextField
            size="small"
            variant="outlined"
            value={tokenRate}
            onChange={(e) => setTokenRate(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                width: 400,
                bgcolor: "white",
              },
            }}
          />
          <LoadingButton
            variant="contained"
            sx={{
              ml: "auto",
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: 1,
              textTransform: "none",
              "&:hover": { boxShadow: 2 },
            }}
            onClick={() => tokenMutation.mutate(tokenRate)}
            loading={tokenMutation.isPending}
          >
            Save
          </LoadingButton>
        </Box>
      </Box>

      {/* Conversion Rates */}
      <Box
        sx={{
          p: 3,
          borderRadius: 1,
          maxWidth: "80%",
          my: 4,
          boxShadow: 1,
        }}
      >
        {Object.entries(conversionRates).map(([key, value]) => (
          <Box key={key} display="flex" alignItems="center" gap={2} mb={2}>
            <Typography sx={{ minWidth: 200 }}>{formatKey(key)}</Typography>
            <TextField
              size="small"
              variant="outlined"
              value={value}
              onChange={(e) =>
                setConversionRates({
                  ...conversionRates,
                  [key]: e.target.value,
                })
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "white",
                  width: 400,
                },
              }}
            />
          </Box>
        ))}

        <LoadingButton
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            ml: "auto",
            boxShadow: 1,
            textTransform: "none",
            "&:hover": { boxShadow: 2 },
          }}
          onClick={() => conversionMutation.mutate(conversionRates)}
          loading={conversionMutation.isPending}
        >
          Save All
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default AddWallet;
